import SharedDialog from '../../../shared/shared-dialog';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { UserKeys, updateDocName } from '../../../api/users-api';
import { handleErrors } from '../../../utils/errors';
import { Doc } from '../../../../types';
import { Dispatch, SetStateAction, useState } from 'react';
import ChangeLogDialog from '../../../shared/change-log-dialog';
import { useStore } from '../../state/zustand-store';
import { useAnalytics } from '../../../shared/analytics/create-events';
import * as analytics from '../../../shared/analytics/analytics.json';
import { ModuleList } from '../../../shared/analytics/module-list';
import {
  DriveRoles,
  isAbleToSeeUIElement,
} from '../../../utils/roles/client-roles';

import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';

interface DriveSearchRenameDialogProps {
  open: boolean;
  handleClose: () => void;
  userEmail: string;
  selectedDoc: Doc;
  searchedDocs: Doc[];
  setSearchedDocs: Dispatch<SetStateAction<Doc[]>>;
  changeLogRoles: string[];
}

export default function DriveSearchRenameDialog({
  open,
  handleClose,
  userEmail,
  selectedDoc,
  searchedDocs,
  setSearchedDocs,
  changeLogRoles,
}: DriveSearchRenameDialogProps) {
  const { createEvent } = useAnalytics();
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');
  const queryClient = useQueryClient();
  const { setSnackbar } = useStore();
  const [newName, setNewName] = useState(selectedDoc.title);

  const isFolder = selectedDoc.mimeType.includes('folder');

  const { mutate, isPending } = useMutation({
    mutationFn: () =>
      updateDocName(userEmail, selectedDoc.id, newName, changeLogMessage),
    onSuccess: () => {
      setSnackbar({
        open: true,
        severity: 'success',
        message: `${isFolder ? 'Folder' : 'Document'} renamed successfully!`,
      });

      const updatedDocs = searchedDocs.map((doc) => {
        if (doc.id === selectedDoc.id) {
          return { ...doc, title: newName };
        }
        return doc;
      });

      setSearchedDocs(updatedDocs);

      if (isFolder) {
        queryClient.invalidateQueries({ queryKey: [UserKeys.DriveFolders] });
      } else {
        queryClient.invalidateQueries({
          queryKey: [UserKeys.DriveDocuments, selectedDoc.owner],
        });
      }

      setChangeLogMessage('');
      handleClose();
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage: `There was an issue renaming the ${
          isFolder ? 'folder' : 'document'
        }. Please refresh and try again.`,
      });
    },
    onSettled: () => {
      createEvent(analytics.drive.drive_search.rename_drive, {
        module: ModuleList.modules.drive.name,
        section: ModuleList.modules.drive.sections.driveSearch,
      });
      setOpenChangeLogDialog(false);
    },
  });

  function handleSubmit() {
    if (isAbleToSeeUIElement(changeLogRoles, DriveRoles.CanRenameDocs.value)) {
      setOpenChangeLogDialog(true);
    } else {
      mutate();
    }
  }

  return (
    <SharedDialog
      open={open}
      handleClose={handleClose}
      title={`Rename ${isFolder ? 'Folder' : 'Document'}`}
      dialogContent={
        <Box>
          <TextField
            fullWidth
            autoFocus
            label={`${isFolder ? 'Folder' : 'Document'} Name`}
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
          />
          <ChangeLogDialog
            open={openChangeLogDialog}
            handleClose={() => setOpenChangeLogDialog(false)}
            changeLogMessage={changeLogMessage}
            setChangeLogMessage={setChangeLogMessage}
            mutate={mutate}
            mutateLoading={isPending}
          />
        </Box>
      }
      primaryButton={{
        title: 'Save Changes',
        action: handleSubmit,
        loading: isPending,
        disabled: selectedDoc.title === newName,
      }}
    />
  );
}
